import { operationalSelectedScopeId } from '../../runtime/operational-selected-scope';
import type {
  FeedbackIssuePacket,
  FeedbackRecord,
  FeedbackRepository,
  FeedbackScope,
  SubmitFeedbackInput,
  TriageFeedbackInput,
} from '@kop/feedback-firebase-adapter';
import { prepareEvidenceImage } from '@kop/media';

const STORAGE_KEY = 'kop-eumseong-feedback-records-v2';

export type {
  FeedbackIssuePacket,
  FeedbackRecord,
  FeedbackRepository,
  FeedbackScope,
  SubmitFeedbackInput,
  TriageFeedbackInput,
};

function readRecords(storage: Storage): FeedbackRecord[] {
  try {
    const parsed = JSON.parse(storage.getItem(STORAGE_KEY) ?? '[]') as unknown;
    return Array.isArray(parsed) ? (parsed as FeedbackRecord[]) : [];
  } catch {
    return [];
  }
}

function saveRecords(storage: Storage, records: FeedbackRecord[]) {
  storage.setItem(STORAGE_KEY, JSON.stringify(records.slice(0, 100)));
}

function classify(description: string, category: string) {
  const protectedArea = /(권한|인증|개인정보|법정|법률|승인|결재|운영 데이터)/u.test(description);
  return {
    severity: protectedArea
      ? ('high' as const)
      : category === 'error'
        ? ('medium' as const)
        : ('low' as const),
    securityClass: /(권한|인증|개인정보)/u.test(description) ? 'security-review' : 'none',
    legalImpact: /(법정|법률)/u.test(description) ? 'review-required' : 'none',
    humanGateRequired: protectedArea,
  };
}

class LocalFeedbackRepository implements FeedbackRepository {
  constructor(
    private readonly storage: Storage,
    private readonly identityId: string,
    private readonly displayName: string,
  ) {}

  async submit(input: SubmitFeedbackInput): Promise<FeedbackRecord> {
    const report = input.report as FeedbackRecord['report'];
    const now = new Date().toISOString();
    const triage = classify(report.description, report.category);
    const record: FeedbackRecord = {
      feedbackId: input.feedbackId,
      version: 1,
      scope: input.scope,
      reporter: { identityId: this.identityId, displayName: this.displayName },
      report,
      status: triage.humanGateRequired ? 'awaiting-approval' : 'received',
      duplicateGroupId: `local-${report.category}-${report.route}`,
      triage,
      createdAt: now,
      updatedAt: now,
    };
    saveRecords(this.storage, [record, ...readRecords(this.storage)]);
    return (await this.getOwn(input.tenantId, input.feedbackId))!;
  }

  async getOwn(_tenantId: string, feedbackId: string) {
    return (
      readRecords(this.storage).find(
        (item) => item.feedbackId === feedbackId && item.reporter.identityId === this.identityId,
      ) ?? null
    );
  }

  async listOwn(scope: FeedbackScope) {
    return readRecords(this.storage).filter(
      (item) =>
        item.reporter.identityId === this.identityId &&
        item.scope.tenantId === scope.tenantId &&
        (scope.kind === 'tenant' ||
          (item.scope.kind !== 'tenant' && item.scope.siteId === scope.siteId)),
    );
  }

  async listTriage(scope: FeedbackScope) {
    return readRecords(this.storage).filter(
      (item) =>
        item.scope.tenantId === scope.tenantId &&
        (scope.kind === 'tenant' ||
          (item.scope.kind !== 'tenant' && item.scope.siteId === scope.siteId)),
    );
  }

  async getIssuePacket(_tenantId: string, feedbackId: string): Promise<FeedbackIssuePacket | null> {
    const record = readRecords(this.storage).find((item) => item.feedbackId === feedbackId);
    if (!record) return null;
    return {
      packetId: `packet-${record.feedbackId}`,
      feedbackId: record.feedbackId,
      title: `[${record.report.category}] ${record.report.description.slice(0, 80)}`,
      sanitizedDescription: record.report.description,
      reproductionSteps: [
        '동일한 역할로 로그인',
        `${record.report.route} 화면 열기`,
        '신고 내용과 같은 조작 실행',
      ],
      expectedBehavior: '업무 중단 없이 명확한 결과가 표시되어야 합니다.',
      observedBehavior: record.report.description,
      testSkeleton: '격리 환경 재현 테스트가 준비되었습니다.',
      externalIssueRef: null,
      productionChangeAllowed: false,
      humanGateRequired: record.triage.humanGateRequired,
      generatedAt: record.createdAt,
    };
  }

  async triage(input: TriageFeedbackInput) {
    const records = readRecords(this.storage);
    const index = records.findIndex((item) => item.feedbackId === input.feedbackId);
    if (index < 0) throw new Error('요청을 찾지 못했습니다.');
    const current = records[index]!;
    if (current.version !== input.expectedVersion)
      throw new Error('다른 관리자가 먼저 변경했습니다. 새로고침 후 다시 시도해 주세요.');
    if (current.triage.humanGateRequired && !input.humanApprovalConfirmed)
      throw new Error('고위험 요청은 사람의 명시적 승인 후에만 상태를 변경할 수 있습니다.');
    const statusByAction = {
      'start-analysis': 'analyzing',
      'request-information': 'needs-information',
      'prepare-reproduction': 'reproducing',
      defer: 'deferred',
      reject: 'rejected',
    } as const;
    const updated = {
      ...current,
      version: current.version + 1,
      status: statusByAction[input.action],
      updatedAt: new Date().toISOString(),
    };
    records[index] = updated;
    saveRecords(this.storage, records);
    return updated;
  }
}

/**
 * Transport shape returned by `@kop/operations-firebase-adapter`'s
 * `createFeedbackOperationsPort`: the exact six-callable production wave
 * (loadFeedbackSetup / submitTextFeedback / listMyFeedback / loadFeedback /
 * listFeedbackTriage / executeFeedbackTriage). Kept local and untyped-at-the-edges to
 * match how the adapter package itself returns callable results (raw `unknown` data),
 * so every field this repository relies on is validated before use below.
 */
type FeedbackOperationsCallablePort = {
  submit(input: Readonly<Record<string, unknown>>): Promise<unknown>;
  listOwn(input: Readonly<Record<string, unknown>>): Promise<unknown>;
  detail(input: Readonly<Record<string, unknown>>): Promise<unknown>;
  listTriage(input: Readonly<Record<string, unknown>>): Promise<unknown>;
  executeTriage(input: Readonly<Record<string, unknown>>): Promise<unknown>;
};

const feedbackSeverities = ['low', 'medium', 'high', 'critical'] as const;

/**
 * The six-callable production wave scopes every request to a `selectedScopeId` string
 * (`tenant:kop` / `site:<id>` / `site:<id>/contractor:<id>`), never a raw resource object. This is a
 * pure, deterministic re-encoding of the scope the caller already supplied -- it does not
 * infer or widen access. The server resolves the selected site through verified configuration; the client never defaults it.
 */
export function feedbackScopeId(scope: FeedbackScope): string | null {
  return operationalSelectedScopeId(scope);
}

/**
 * Validates and translates a server feedback projection into this feature's `FeedbackRecord`
 * shape. The same translator handles a `loadFeedback` detail response and a single row of a
 * `listMyFeedback` / `listFeedbackTriage` page, because the wave returns the identical
 * projection in both places. Every field is read from the server response; nothing here derives
 * role, site, capability, or permission information client-side. Malformed responses throw
 * rather than silently substituting a default (e.g. a missing severity is never coerced to
 * "low").
 */
export function toFeedbackRecord(scope: FeedbackScope, value: unknown): FeedbackRecord {
  const malformed = () => new Error('서버 응답 형식을 확인할 수 없습니다.');
  if (!value || typeof value !== 'object') throw malformed();
  const feedback = value as Record<string, unknown>;
  const { feedbackId, version, status, report, triage, createdAt, updatedAt } = feedback;
  if (
    typeof feedbackId !== 'string' ||
    !feedbackId ||
    typeof version !== 'number' ||
    typeof status !== 'string' ||
    !status ||
    !report ||
    typeof report !== 'object' ||
    !triage ||
    typeof triage !== 'object' ||
    typeof createdAt !== 'string' ||
    typeof updatedAt !== 'string'
  )
    throw malformed();
  const reportValue = report as Record<string, unknown>;
  const triageValue = triage as Record<string, unknown>;
  if (
    typeof reportValue.category !== 'string' ||
    typeof reportValue.description !== 'string' ||
    typeof reportValue.route !== 'string' ||
    typeof reportValue.occurredAt !== 'string' ||
    typeof triageValue.severity !== 'string' ||
    !feedbackSeverities.includes(triageValue.severity as (typeof feedbackSeverities)[number]) ||
    typeof triageValue.securityClass !== 'string' ||
    typeof triageValue.legalImpact !== 'string' ||
    typeof triageValue.humanGateRequired !== 'boolean'
  )
    throw malformed();
  const reporterValue = feedback.reporter as Record<string, unknown> | undefined;
  const allowedActionsValue = feedback.allowedActions;
  return {
    feedbackId,
    version,
    scope,
    reporter: {
      identityId: typeof reporterValue?.identityId === 'string' ? reporterValue.identityId : '',
      displayName:
        typeof reporterValue?.displayName === 'string' ? reporterValue.displayName : '',
    },
    report: {
      category: reportValue.category,
      description: reportValue.description,
      route: reportValue.route,
      occurredAt: reportValue.occurredAt,
    },
    status,
    // The production wave does not surface a duplicate-group id on this projection. Falling
    // back to the record's own id (instead of a shared constant) keeps every record its own
    // singleton group rather than implying a duplicate relationship the server never reported.
    duplicateGroupId: typeof feedback.duplicateGroupId === 'string' ? feedback.duplicateGroupId : feedbackId,
    triage: {
      severity: triageValue.severity as FeedbackRecord['triage']['severity'],
      securityClass: triageValue.securityClass,
      legalImpact: triageValue.legalImpact,
      humanGateRequired: triageValue.humanGateRequired,
    },
    createdAt,
    updatedAt,
    // Absent (rather than defaulted permissive) when the server omits it, so the UI's
    // allowed-actions gate fails closed instead of assuming every action is permitted.
    allowedActions: Array.isArray(allowedActionsValue)
      ? allowedActionsValue.filter((action): action is string => typeof action === 'string')
      : [],
  };
}

/**
 * Wires the 건의사항 (product feedback) screens to the deployed
 * `feedback-operations-production-wave` callables via the shared, already-authenticated
 * Firebase app (App Check attaches automatically because that app owns App Check init).
 * Every read here is a genuine round trip: submit/triage never report success without a
 * server-confirmed receipt, and nothing is ever written back to local storage as a fallback.
 */
export class ProductionFeedbackRepository implements FeedbackRepository {
  private lastScope: FeedbackScope | null = null;

  constructor(private readonly port: FeedbackOperationsCallablePort) {}

  private async loadDetailRecord(
    scope: FeedbackScope,
    feedbackId: string,
  ): Promise<FeedbackRecord | null> {
    const selectedScopeId = feedbackScopeId(scope);
    if (!selectedScopeId) throw new Error('허용되지 않은 범위입니다.');
    const response = (await this.port.detail({ selectedScopeId, feedbackId })) as
      | { feedback?: unknown }
      | null
      | undefined;
    const feedback = response?.feedback;
    if (feedback === null || feedback === undefined) return null;
    return toFeedbackRecord(scope, feedback);
  }

  private async loadPage(scope: FeedbackScope, mode: 'own' | 'triage'): Promise<FeedbackRecord[]> {
    const selectedScopeId = feedbackScopeId(scope);
    if (!selectedScopeId) return [];
    this.lastScope = scope;
    const page =
      mode === 'own'
        ? await this.port.listOwn({ selectedScopeId, pageSize: 25 })
        : await this.port.listTriage({ selectedScopeId, pageSize: 25 });
    const items = (page as { items?: unknown } | null | undefined)?.items;
    if (!Array.isArray(items)) throw new Error('목록 응답 형식을 확인할 수 없습니다.');
    // A list page now carries the same projection `loadFeedback` returns for each row, so one
    // call serves the whole page. Rows go through the identical translator the detail response
    // does: a row missing a field the screen renders throws and surfaces as an error, rather
    // than being quietly patched up with a default or re-hydrated one request at a time.
    return items.map((item) => toFeedbackRecord(scope, item));
  }

  async submit(input: SubmitFeedbackInput): Promise<FeedbackRecord> {
    const selectedScopeId = feedbackScopeId(input.scope);
    if (!selectedScopeId) throw new Error('허용되지 않은 범위입니다.');
    this.lastScope = input.scope;
    await this.port.submit({
      selectedScopeId,
      feedbackId: input.feedbackId,
      commandId: input.commandId,
      idempotencyKey: input.idempotencyKey,
      report: input.report,
    });
    const persisted = await this.loadDetailRecord(input.scope, input.feedbackId);
    if (!persisted) throw new Error('접수 결과를 서버에서 확인하지 못했습니다. 잠시 후 다시 확인해 주세요.');
    return persisted;
  }

  async getOwn(_tenantId: string, feedbackId: string): Promise<FeedbackRecord | null> {
    if (!this.lastScope) throw new Error('조회 범위를 확인할 수 없습니다.');
    return this.loadDetailRecord(this.lastScope, feedbackId);
  }

  async listOwn(scope: FeedbackScope): Promise<FeedbackRecord[]> {
    return this.loadPage(scope, 'own');
  }

  async listTriage(scope: FeedbackScope): Promise<FeedbackRecord[]> {
    return this.loadPage(scope, 'triage');
  }

  async getIssuePacket(): Promise<FeedbackIssuePacket | null> {
    // The deployed six-callable wave has no issue-packet endpoint. Failing closed (returning
    // null) is correct here: the local fixture repository's packet is synthesised client-side
    // and must not be reproduced against production data.
    return null;
  }

  async triage(input: TriageFeedbackInput): Promise<FeedbackRecord> {
    const selectedScopeId = feedbackScopeId(input.scope);
    if (!selectedScopeId) throw new Error('허용되지 않은 범위입니다.');
    this.lastScope = input.scope;
    await this.port.executeTriage({
      selectedScopeId,
      feedbackId: input.feedbackId,
      commandId: input.commandId,
      idempotencyKey: input.idempotencyKey,
      expectedVersion: input.expectedVersion,
      action: input.action,
      note: input.note,
      humanApprovalConfirmed: input.humanApprovalConfirmed,
    });
    const persisted = await this.loadDetailRecord(input.scope, input.feedbackId);
    if (!persisted) throw new Error('분류 결과를 서버에서 확인하지 못했습니다.');
    return persisted;
  }
}

function loopback(host: string): boolean {
  return host === '127.0.0.1' || host === 'localhost' || host === '::1';
}

export async function createFeedbackRepository(input: {
  storage: Storage;
  identityId: string;
  displayName: string;
}): Promise<FeedbackRepository> {
  if (import.meta.env.VITE_FEEDBACK_DATA_ADAPTER === 'firebase') {
    const config = {
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? '',
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY ?? '',
      appId: import.meta.env.VITE_FIREBASE_APP_ID ?? '',
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? '',
      appCheckSiteKey: import.meta.env.VITE_FIREBASE_APPCHECK_SITE_KEY ?? '',
    };
    if (Object.values(config).some((value) => value.trim().length === 0))
      throw new Error('시스템 개선 관리 Firebase 환경 설정이 완전하지 않습니다.');
    // Constructed lazily here, on first use (component mount, after sign-in) -- never at
    // module scope -- and only via the shared authenticated app that createFeedbackOperationsPort
    // reuses internally, never a second Firebase app instance.
    const { createFeedbackOperationsPort } = await import('@kop/operations-firebase-adapter');
    return new ProductionFeedbackRepository(
      createFeedbackOperationsPort(config) as FeedbackOperationsCallablePort,
    );
  }
  if (import.meta.env.VITE_FEEDBACK_DATA_ADAPTER !== 'firebase-emulator')
    return new LocalFeedbackRepository(input.storage, input.identityId, input.displayName);
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID ?? '';
  const firestoreHost = import.meta.env.VITE_FIRESTORE_EMULATOR_HOST ?? '127.0.0.1';
  const functionsHost = import.meta.env.VITE_FUNCTIONS_EMULATOR_HOST ?? '127.0.0.1';
  if (!projectId.startsWith('demo-') || !loopback(firestoreHost) || !loopback(functionsHost))
    throw new Error(
      '시스템 개선 관리 Firebase 연결은 demo 프로젝트와 로컬 에뮬레이터에서만 허용됩니다.',
    );
  const { createFirebaseEmulatorFeedbackRepository } =
    await import('@kop/feedback-firebase-adapter');
  return createFirebaseEmulatorFeedbackRepository({
    projectId,
    firestore: {
      host: firestoreHost,
      port: Number(import.meta.env.VITE_FIRESTORE_EMULATOR_PORT ?? 8080),
    },
    functions: {
      host: functionsHost,
      port: Number(import.meta.env.VITE_FUNCTIONS_EMULATOR_PORT ?? 5001),
    },
  });
}

export async function uploadFeedbackScreenshot(input: {
  file: Blob;
  feedbackId: string;
  scope: FeedbackScope;
}): Promise<{ manifestId: string; evidenceSha256: string }> {
  if (import.meta.env.VITE_FEEDBACK_DATA_ADAPTER !== 'firebase-emulator')
    throw new Error('화면 캡처는 신뢰 미디어 서버 연결 후에만 접수할 수 있습니다.');
  if (input.scope.kind === 'tenant' || input.scope.siteId !== 'site-eumseong')
    throw new Error('음성공장 범위의 화면 캡처만 접수할 수 있습니다.');
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID ?? '';
  const functionsHost = import.meta.env.VITE_FUNCTIONS_EMULATOR_HOST ?? '127.0.0.1';
  const firestoreHost = import.meta.env.VITE_FIRESTORE_EMULATOR_HOST ?? '127.0.0.1';
  const storageHost = import.meta.env.VITE_STORAGE_EMULATOR_HOST ?? '127.0.0.1';
  if (
    !projectId.startsWith('demo-') ||
    !loopback(functionsHost) ||
    !loopback(firestoreHost) ||
    !loopback(storageHost)
  )
    throw new Error('신뢰 미디어 연결 구성이 확인되지 않았습니다.');
  const { createMediaFirebaseEmulatorAdapter } = await import('@kop/media-firebase-adapter');
  const prepared = await prepareEvidenceImage(input.file, 'routine_inspection');
  const manifestId = `feedback-${input.feedbackId}`;
  const adapter = createMediaFirebaseEmulatorAdapter({
    projectId: projectId as `demo-${string}`,
    functions: {
      host: functionsHost as '127.0.0.1',
      port: Number(import.meta.env.VITE_FUNCTIONS_EMULATOR_PORT ?? 5001),
    },
    firestore: {
      host: firestoreHost as '127.0.0.1',
      port: Number(import.meta.env.VITE_FIRESTORE_EMULATOR_PORT ?? 8080),
    },
    storage: {
      host: storageHost as '127.0.0.1',
      port: Number(import.meta.env.VITE_STORAGE_EMULATOR_PORT ?? 9199),
    },
  });
  const committed = await adapter.upload({
    tenantId: input.scope.tenantId,
    siteId: 'site-eumseong',
    contractorId: input.scope.kind === 'contractor' ? input.scope.contractorId : null,
    manifestId,
    evidenceId: input.feedbackId,
    purpose: 'feedback-evidence',
    evidenceClass: 'routine_inspection',
    prepared,
  });
  const evidence = committed.parts.find(
    (part) => part.variant === 'evidence' && part.state === 'verified',
  );
  if (!evidence) throw new Error('서버가 화면 캡처 증거를 확정하지 않았습니다.');
  return { manifestId, evidenceSha256: evidence.sha256 };
}

/** Visibility comes from a current server account binding, never from a site role or email match. */
export async function loadFeedbackDeveloperAccess(identityId: string): Promise<boolean> {
  if (import.meta.env.VITE_FEEDBACK_DATA_ADAPTER !== 'firebase') return false;
  const { createFeedbackOperationsPort } = await import('@kop/operations-firebase-adapter');
  const port = createFeedbackOperationsPort({ projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? '',
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY ?? '', appId: import.meta.env.VITE_FIREBASE_APP_ID ?? '',
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? '', appCheckSiteKey: import.meta.env.VITE_FIREBASE_APPCHECK_SITE_KEY ?? '' });
  const response = await port.setup('tenant:kop') as { developerAccount?: boolean; reporter?: { identityId?: string } };
  return response.developerAccount === true && response.reporter?.identityId === identityId;
}
